import React, { useMemo, useState } from 'react';
import { useAuthStore } from '../../../store/authStore';
import { useClubStore } from '../../../store/clubStore';
import { useDialogStore } from '../../../store/dialogStore';
import { useGlobalStore } from '../../../store/globalStore';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { Navigate, useNavigate } from 'react-router-dom';
import PasswordBlur from './PasswordBlur';
import { Button } from '../../../components/ui/button';
import axiosInstance from '../../../axiosConfig';

const ClubsPage: React.FC = () => {
    const role = useAuthStore((state) => state.role);
    const clubs = useClubStore((state) => state.clubs);
    const navigate = useNavigate();
    const setToken = useAuthStore((state) => state.setToken);
    const setMyClub = useClubStore((state) => state.setMyClub);
    const setClubLoginTime = useClubStore((state) => state.setClubLoginTime);
    const [query, setQuery] = useState('');
    const { openDialog, closeDialog, setCloseButtonLabel, setSaveButtonLabel } = useDialogStore();
    const setLoading = useGlobalStore((state) => state.setLoading);

    const filteredClubs = useMemo(() => {
        const normalizedQuery = query.trim().toLowerCase();
        if (!normalizedQuery) return clubs;
        return clubs.filter((club) => {
            const name = (club.name || '').toLowerCase();
            const sender = (club.sender_name || '').toLowerCase();
            const number = String(club.club_number || '').toLowerCase();
            return (
                name.includes(normalizedQuery) ||
                sender.includes(normalizedQuery) ||
                number.includes(normalizedQuery)
            );
        });
    }, [clubs, query]);

    const switchToClub = async (club_id: number) => {
        try {
            const response = await axiosInstance.post('/switch/club', { club_id });
            const res = response.data;
            setToken(res.token);
            await fetchMyClubData();
            setClubLoginTime(club_id);
            navigate('/league');
        } catch (error) {
            console.error('Fehler beim Wechseln zum Verein:', error);
        }
    };

    const fetchMyClubData = async () => {
        try {
            const response = await axiosInstance.get('/club');
            const res = response.data;
            setMyClub(res);
        } catch (error) {
            console.error('Fehler beim Abrufen der Club-Daten:', error);
        }
    };

    const confirmSendCredentials = (club: { id: number; name: string }) => {
        setCloseButtonLabel('Nein');
        setSaveButtonLabel('Ja');

        const content = (
            <div>
                <div className="font-bold mb-2">Bestätigung</div>
                <p>
                    Sind Sie sicher, dass Sie die Zugangsdaten für den Club{' '}
                    <span className="font-bold">{club.name}</span>{' '}senden möchten?
                </p>
            </div>
        );

        openDialog(
            content,
            async () => {
                try {
                    setLoading(true);
                    await axiosInstance.post('/association/club/send-credentials', { id: club.id });
                    closeDialog();
                    const successContent = (
                        <div>
                            <div className="font-bold mb-2">Erfolg</div>
                            <p className="text-green-600 font-bold">E-Mail wurde erfolgreich gesendet.</p>
                        </div>
                    );
                    openDialog(successContent, () => { }, true);
                } catch (error) {
                    // Fehlerbehandlung erfolgt global über Interceptors
                } finally {
                    setLoading(false);
                    setCloseButtonLabel('Abbruch');
                    setSaveButtonLabel('Weiter');
                }
            },
        );
    };


    if (role !== 'admin') {
        return <Navigate to="/tournaments" />;
    }

    return (
        <div className="px-6">
            <div className="mb-4 sticky border border-gray-300 shadow-sm rounded-b-md top-14 bg-white p-4 z-10">
                <h1 className="text-xl font-bold">Clubs verwalten</h1>
                <p className="text-sm text-gray-600">Liste der Clubs</p>
                <div className="mt-2">
                    <input
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Suche nach Name, Clubnummer, Absender"
                        aria-label="Clubs suchen"
                        autoComplete="off"
                        className="w-full focus:outline-none active:outline-none focus:ring-0 focus:border-gray-400 border rounded-md px-3 py-1.5"
                    />
                </div>
            </div>

            <div className="bg-white border rounded-md shadow-sm">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead className="w-[20px]">ID</TableHead>
                            <TableHead className="w-[100px]">Vereinsnummer</TableHead>
                            <TableHead className="w-[300px]">Name</TableHead>
                            <TableHead className="w-[200px]">E-Mail</TableHead>
                            <TableHead>Passwort</TableHead>
                            <TableHead>Aktionen</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {filteredClubs.map((club) => (
                            <TableRow key={club.id} className={`hover:bg-gray-50 ${club.loginTime ? 'bg-green-50 hover:bg-green-50' : ''}`}>
                                <TableCell>{club.id}</TableCell>
                                <TableCell className="font-bold">{club.club_number}</TableCell>
                                <TableCell>
                                    <div className="font-bold">{club.name}</div>
                                    <div className="text-xs text-gray-500">{club.sender_name ?? '-'}</div>
                                    {club.loginTime && <div className="text-xs text-blue-500 mt-1 font-medium">Letzte Anmeldung: {club.loginTime}</div>}
                                </TableCell>
                                <TableCell className="font-bold">{club.email}</TableCell>
                                <TableCell>
                                    <PasswordBlur value={club.password} />
                                </TableCell>
                                <TableCell>
                                    <div className="flex items-center gap-2">
                                        <Button onClick={() => switchToClub(club.id)} size="sm" variant="outline">Anmelden</Button>
                                        <Button onClick={() => confirmSendCredentials({ id: club.id, name: club.name })} size="sm" variant="outline">Zugangsdaten senden</Button>
                                    </div>
                                </TableCell>
                            </TableRow>
                        ))}
                        {filteredClubs.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={6} className="text-center text-gray-500">
                                    Keine Daten vorhanden.
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </div>
        </div>
    );
};

export default ClubsPage;



